'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

const DISCLOSURE_BANNER_SELECTOR = '[data-disclosure-banner]';

/**
 * Pixels of the viewport's bottom edge the legal disclosure banner covers, or 0 on
 * a page without one.
 *
 * The toast and the banner both pin to the bottom at `z-25`, and the banner renders
 * after `main`. Equal z-index makes paint order DOM order, so the banner covers the
 * toast rather than the reverse - the legal copy stays readable and the toast's
 * close button and CTA go under it. Lifting the toast clear of the banner is the
 * fix, and this is the distance to lift it.
 *
 * Measured from the banner's top edge, not from its height. The banner starts
 * translated below the fold when it is waiting for the hero to scroll past, and its
 * height is its full height the whole time. A height read would lift the toast over
 * empty space; a rect read reports 0 until the banner is really on screen, because
 * `getBoundingClientRect` includes the transform.
 *
 * Three things move the number after the first read, hence three subscriptions:
 *
 * - The banner's own slide in and out, which `transitionend` reports.
 * - A rewrap of the legal copy, which changes the banner's height below `sm` where
 *   it is `min-h-min`. A rewrap needs a width change, so `resize` covers it, and
 *   covers rotation with it.
 * - The visitor closing it. That one has to come from the banner's event rather than
 *   a re-measure, because the banner dispatches before React removes the node.
 */
export const useDisclosureBannerOverlap = (): number => {
  const [overlap, setOverlap] = useState(0);
  // A client-side navigation reuses the module instance, so a hook keyed on mount
  // alone would hold the banner it found on the page the visitor arrived on - and a
  // page that had none would never pick one up.
  const pathname = usePathname();

  useEffect(() => {
    const banner = document.querySelector(DISCLOSURE_BANNER_SELECTOR);
    if (!banner) {
      setOverlap(0);
      return;
    }

    const measure = () => setOverlap(Math.max(0, window.innerHeight - banner.getBoundingClientRect().top));
    measure();

    const stopMeasuring = () => {
      banner.removeEventListener('transitionend', measure);
      window.removeEventListener('resize', measure);
    };
    const handleDismissed = () => {
      // The banner leaves the DOM after this event, so no later event can safely
      // measure the same node.
      stopMeasuring();
      setOverlap(0);
    };
    banner.addEventListener('transitionend', measure);
    window.addEventListener('resize', measure);
    window.addEventListener('disclosureBannerDismissed', handleDismissed);

    return () => {
      stopMeasuring();
      window.removeEventListener('disclosureBannerDismissed', handleDismissed);
    };
  }, [pathname]);

  return overlap;
};
